import { Segmented } from "@/components/ui/segmented";
import { HazardPercentList } from "@/components/assessment/results/HazardPercentList";
import { PendingScore } from "@/components/assessment/results/PendingScore";
import { hazardKeyLabel, type AssetHazardResult, type HazardKey } from "@/lib/results";
import { SCENARIO_OPTIONS, getResultsForHorizon } from "@/lib/results-filters";
import { computeTakeaways } from "@/lib/takeaways";
import { computeHorizonDeltas, hazardsWithScoresForHorizon } from "@/lib/dashboard-stats";
import { HAZARD_TYPES, type ClimateScenario, type HazardType } from "@shared/scoring/types";
import type { Asset } from "@/lib/assets";

interface RiskHorizonColumnsProps {
  /** Null while the slow fetch group is still in flight. */
  results: AssetHazardResult[] | null;
  error: string | null;
  assets: Asset[];
  stageImportance: Record<string, number>;
  scenario: ClimateScenario;
  onScenarioChange: (scenario: ClimateScenario) => void;
}

function Takeaway({ label, hazard }: { label: string; hazard: HazardKey | null }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-bold uppercase leading-4 tracking-[0.08em] text-muted-foreground">
        {label}
      </span>
      {hazard ? (
        <span className="text-sm font-semibold">{hazardKeyLabel(hazard)}</span>
      ) : (
        <PendingScore />
      )}
    </div>
  );
}

/**
 * One horizon's column. `hazards` narrows only the percent list — it reaches
 * only percentAtHighRiskPerHazard (takeaways.ts:53). Do NOT pre-filter `rows`
 * instead: the three "worst hazard" takeaways scan every row, which is
 * correct because a hazard with no data at this horizon has no `computed`
 * rows and can never win — and pre-filtering would change what Tab 2 and
 * Tab 4 compute through this same shared function.
 *
 * Stacked with flex-col, never a `lg:grid-cols-*`: inside a half-width column
 * `lg:` responds to the viewport, not this container (see
 * TakeawayCards.tsx:81-90 — this was a real bug).
 */
function HorizonColumn({
  title,
  caption,
  rows,
  assets,
  stageImportance,
  hazards,
}: {
  title: string;
  caption?: string;
  rows: AssetHazardResult[];
  assets: Asset[];
  stageImportance: Record<string, number>;
  hazards: readonly HazardType[];
}) {
  const takeaways = computeTakeaways(rows, assets, stageImportance, hazards);

  return (
    <div className="flex flex-1 flex-col gap-4 rounded-xl border border-border bg-card p-6 shadow-sm">
      <div className="flex flex-col gap-0.5">
        <h3 className="text-base font-bold leading-6">{title}</h3>
        {caption && <p className="text-xs leading-4 text-muted-foreground">{caption}</p>}
      </div>

      <div className="flex flex-col gap-3">
        <Takeaway label="Most widespread hazard" hazard={takeaways.mostAssetsAtRisk} />
        <Takeaway label="Worst for key assets" hazard={takeaways.riskiestForTopAssets} />
        <Takeaway label="Worst for top value chain stage" hazard={takeaways.riskiestForTopValueChainStage} />
      </div>

      <div className="flex flex-col gap-2 border-t border-border pt-4">
        <span className="text-xs font-bold uppercase leading-4 tracking-[0.08em] text-muted-foreground">
          % at high risk, per hazard
        </span>
        <HazardPercentList perHazardPct={takeaways.percentAtHighRisk} />
      </div>
    </div>
  );
}

function ColumnSkeleton() {
  return (
    <div className="flex flex-1 flex-col gap-4 rounded-xl border border-border bg-card p-6 shadow-sm">
      <div className="h-5 w-24 animate-pulse rounded bg-muted" />
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="h-4 w-full animate-pulse rounded bg-muted" />
      ))}
    </div>
  );
}

function DeltaLine({ results, scenario }: { results: AssetHazardResult[]; scenario: ClimateScenario }) {
  // Top two growing hazards only. Nothing growing -> no line at all, rather
  // than a "no change" message that would overstate what the data shows.
  const growing = computeHorizonDeltas(results, scenario)
    .filter((d) => d.deltaPct > 0)
    .slice(0, 2);
  if (growing.length === 0) return null;

  const phrases = growing.map((d) => `${hazardKeyLabel(d.key)} (+${d.deltaPct}pp)`);

  return (
    <p className="text-sm leading-5 text-muted-foreground">
      High-risk share grows most for {phrases.join(" and ")} by 2050.
    </p>
  );
}

export function RiskHorizonColumns({
  results,
  error,
  assets,
  stageImportance,
  scenario,
  onScenarioChange,
}: RiskHorizonColumnsProps) {
  const futureHazards = results ? hazardsWithScoresForHorizon(results, "2050", scenario) : [];

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-bold leading-7">Risk signal</h2>
        {/* `subtle` (the default): ResultsFilters renders this same SSP control
            that way, and segmented.tsx reserves `solid` for a page's main mode
            switch. Single-select here — onToggle replaces the selection. */}
        <Segmented
          options={SCENARIO_OPTIONS}
          isActive={(v) => scenario === v}
          onToggle={(v) => onScenarioChange(v as ClimateScenario)}
        />
      </div>

      {error && <p className="text-sm font-medium text-destructive">Couldn&apos;t load risk results: {error}</p>}

      <div className="flex flex-col gap-4 lg:flex-row">
        {/* Error is checked BEFORE loading, like CoverageRow: on failure
            `results` stays null forever, so testing it first would render two
            permanently animating skeletons underneath the error message. On
            error there are no column placeholders at all. */}
        {error ? null : results === null ? (
          <>
            <ColumnSkeleton />
            <ColumnSkeleton />
          </>
        ) : (
          <>
            {/* All six hazards (precipitation as MAM and OND lines, plus a main-season line for Malawi assets). */}
            <HorizonColumn
              title="Current"
              rows={getResultsForHorizon(results, "historical", scenario)}
              assets={assets}
              stageImportance={stageImportance}
              hazards={HAZARD_TYPES}
            />
            <HorizonColumn
              title="2050"
              caption={`2050 projections available for ${futureHazards.length} of ${HAZARD_TYPES.length} hazards`}
              rows={getResultsForHorizon(results, "2050", scenario)}
              assets={assets}
              stageImportance={stageImportance}
              hazards={futureHazards}
            />
          </>
        )}
      </div>

      {results !== null && !error && <DeltaLine results={results} scenario={scenario} />}
    </section>
  );
}
